import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_CATEGORIES = ['FOREX', 'STOCKS', 'CRYPTO', 'COMMODITIES', 'METALS', 'INDICES', 'ENERGY'];
const ALL_TIMEFRAMES = ['1m', '5m', '15m', '30m', '1H', '4H', '1D', '1W'];
const ALL_INDICATORS = ['RSI', 'MACD', 'BOLLINGER', 'STOCHASTIC', 'ATR', 'VOLUME', 'SMA', 'EMA'];
const ALL_DRAWING_TOOLS = ['FIBONACCI', 'TREND_LINE', 'HORIZONTAL_LINE', 'VERTICAL_LINE'];
const CHART_TYPES = ['candlestick', 'line', 'area'];
const ORDER_TYPES = ['MARKET', 'LIMIT', 'STOP', 'STOP_LOSS', 'TAKE_PROFIT'];

const INDICATOR_LABELS = {
  RSI: 'RSI', MACD: 'MACD', BOLLINGER: 'Bollinger Bands', STOCHASTIC: 'Stochastic',
  ATR: 'ATR', VOLUME: 'Volume', SMA: 'SMA', EMA: 'EMA',
};
const DRAWING_LABELS = {
  FIBONACCI: 'Fibonacci Retracement', TREND_LINE: 'Trend Line',
  HORIZONTAL_LINE: 'Horizontal Line', VERTICAL_LINE: 'Vertical Line',
};
const ORDER_LABELS = {
  MARKET: 'Market', LIMIT: 'Limit', STOP: 'Stop', STOP_LOSS: 'Stop Loss', TAKE_PROFIT: 'Take Profit',
};

const ToggleGrid = ({ items, labels, values, onToggle, testPrefix, cols = 'grid-cols-2 md:grid-cols-4' }) => (
  <div className={`grid ${cols} gap-3`}>
    {items.map(it => (
      <div key={it} className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg bg-white">
        <Switch
          checked={values?.includes(it)}
          onCheckedChange={() => onToggle(it)}
          data-testid={`${testPrefix}-${it}`}
        />
        <Label className="text-sm">{labels ? labels[it] : it}</Label>
      </div>
    ))}
  </div>
);

const ConfigSections = ({ scope, setScope, prefix, showCategories }) => {
  const toggleList = (field, value) => {
    const list = scope[field] || [];
    const next = list.includes(value) ? list.filter(x => x !== value) : [...list, value];
    setScope({ ...scope, [field]: next });
  };

  return (
    <div className="space-y-6">
      {showCategories && (
        <div className="bg-white p-6 rounded-xl border border-slate-200">
          <h2 className="text-xl font-bold mb-4 text-brand-primary">Enabled Asset Categories</h2>
          <ToggleGrid
            items={ALL_CATEGORIES}
            values={scope.enabled_categories}
            onToggle={(v) => toggleList('enabled_categories', v)}
            testPrefix={`${prefix}-cat`}
          />
        </div>
      )}

      <div className="bg-white p-6 rounded-xl border border-slate-200">
        <h2 className="text-xl font-bold mb-4 text-brand-primary">Timeframes</h2>
        <ToggleGrid
          items={ALL_TIMEFRAMES}
          values={scope.enabled_timeframes}
          onToggle={(v) => toggleList('enabled_timeframes', v)}
          testPrefix={`${prefix}-tf`}
          cols="grid-cols-4 md:grid-cols-8"
        />
        <div className="mt-4 max-w-xs">
          <Label>Default Timeframe</Label>
          <Select value={scope.default_timeframe} onValueChange={v => setScope({ ...scope, default_timeframe: v })}>
            <SelectTrigger data-testid={`${prefix}-default-tf`} className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(scope.enabled_timeframes || []).map(tf => <SelectItem key={tf} value={tf}>{tf}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-200">
        <h2 className="text-xl font-bold mb-4 text-brand-primary">Chart Types</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="mb-2 block">Available</Label>
            <div className="space-y-2">
              {CHART_TYPES.map(ct => (
                <div key={ct} className="flex items-center gap-2">
                  <Switch
                    checked={scope.available_chart_types?.includes(ct)}
                    onCheckedChange={() => toggleList('available_chart_types', ct)}
                    data-testid={`${prefix}-charttype-${ct}`}
                  />
                  <Label className="capitalize">{ct}</Label>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Default Chart Type</Label>
            <Select value={scope.default_chart_type} onValueChange={v => setScope({ ...scope, default_chart_type: v })}>
              <SelectTrigger data-testid={`${prefix}-default-charttype`} className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(scope.available_chart_types || []).map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-200">
        <h2 className="text-xl font-bold mb-4 text-brand-primary">Technical Indicators</h2>
        <ToggleGrid
          items={ALL_INDICATORS}
          labels={INDICATOR_LABELS}
          values={scope.enabled_indicators}
          onToggle={(v) => toggleList('enabled_indicators', v)}
          testPrefix={`${prefix}-ind`}
        />
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-200">
        <h2 className="text-xl font-bold mb-4 text-brand-primary">Drawing Tools</h2>
        <ToggleGrid
          items={ALL_DRAWING_TOOLS}
          labels={DRAWING_LABELS}
          values={scope.enabled_drawing_tools}
          onToggle={(v) => toggleList('enabled_drawing_tools', v)}
          testPrefix={`${prefix}-tool`}
        />
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-200">
        <h2 className="text-xl font-bold mb-4 text-brand-primary">Order Types</h2>
        <ToggleGrid
          items={ORDER_TYPES}
          labels={ORDER_LABELS}
          values={scope.enabled_order_types}
          onToggle={(v) => toggleList('enabled_order_types', v)}
          testPrefix={`${prefix}-ord`}
          cols="grid-cols-2 md:grid-cols-5"
        />
      </div>
    </div>
  );
};

const AdminWebTraderSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/webtrader/settings`);
      setSettings(response.data);
    } catch (e) {
      toast.error('Failed to load WebTrader settings');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSettings(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/webtrader/settings`, {
        enabled_categories: settings.enabled_categories,
        enabled_timeframes: settings.enabled_timeframes,
        default_timeframe: settings.default_timeframe,
        default_chart_type: settings.default_chart_type,
        available_chart_types: settings.available_chart_types,
        enabled_indicators: settings.enabled_indicators,
        default_indicators: settings.default_indicators,
        enabled_drawing_tools: settings.enabled_drawing_tools,
        enabled_order_types: settings.enabled_order_types,
        demo_settings: settings.demo_settings,
      });
      toast.success('WebTrader settings saved. Audit log created.');
    } catch (e) {
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="p-8"><div className="loader"></div></div>;

  const demoScope = settings.demo_settings || {};
  const setDemoScope = (next) => setSettings({ ...settings, demo_settings: next });

  return (
    <div className="p-8 space-y-6" data-testid="admin-webtrader-settings-page">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">WebTrader Settings</h1>
          <p className="text-slate-600 mt-1">Configure the trading environment available to clients</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            if (!window.confirm('Seed default instruments and legal documents into this environment? Safe to run multiple times.')) return;
            try {
              const r = await axios.post(`${API}/admin/system/seed-defaults`);
              toast.success(`Seeded ${r.data.instruments_written || 0} instruments and ${r.data.legal_documents_inserted || 0} legal docs.`);
            } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
          }} data-testid="seed-defaults-btn">Seed Defaults</Button>
          <Button onClick={save} disabled={saving} data-testid="save-webtrader-settings">
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
        Admin controls WebTrader <strong>configuration and availability</strong>. Market prices come from the data provider and cannot be manipulated. All changes are recorded in the audit trail. Demo/Sandbox accounts follow the <strong>Demo Configuration</strong> tab independently from live accounts.
      </div>

      <Tabs defaultValue="live" className="w-full">
        <TabsList data-testid="webt-scope-tabs">
          <TabsTrigger value="live" data-testid="webt-tab-live">Live Accounts</TabsTrigger>
          <TabsTrigger value="demo" data-testid="webt-tab-demo">Demo / Sandbox</TabsTrigger>
        </TabsList>
        <TabsContent value="live" className="mt-6">
          <ConfigSections
            scope={settings}
            setScope={(next) => setSettings(next)}
            prefix="webt"
            showCategories
          />
        </TabsContent>
        <TabsContent value="demo" className="mt-6">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
            These settings apply only to <strong>DEMO / Sandbox accounts</strong>. Great for restricting trainee traders to a subset of features while keeping live accounts fully unlocked.
          </div>
          <ConfigSections
            scope={demoScope}
            setScope={setDemoScope}
            prefix="webt-demo"
            showCategories={false}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminWebTraderSettings;
